import React, { useCallback, useMemo, useState } from 'react';
import { isNull, isUndefined, set } from 'lodash/fp';

import { Icon } from '../../../../shared/icon/Icon';
import { Delete, Tick } from '../../../../shared/icons';
import { CustomTooltip } from '../../../../shared/tooltip';
import styles from './TableInstanceFilters.module.scss';
import { TextFilterType } from '../../store/filterTypes';
import { initialTextFilter } from '../../../../constants/instanceFilters';
import { ToggleFilter } from './ToggleFilter';

interface TextFilterProps {
    updateFilter: (value: TextFilterType) => void;
    removeFilter: () => void;
    filter: TextFilterType | null;
    testId: string;
}

export const TextFilter: React.FC<TextFilterProps> = ({ updateFilter, removeFilter, filter, testId }) => {
    const textFilter = useMemo(() => isNull(filter) ? initialTextFilter : filter, [filter]);
    const { isFuzzyMatchFilter, value } = textFilter;
    const [filterTextValue, setFilterTextValue] = useState<string>(value || '');

    const updateTextFilter = useCallback((key: keyof TextFilterType, value: string | boolean) => {
        if ((key === 'isFuzzyMatchFilter' && !value && filterTextValue === '') || (key === 'value' && value === '')) {
            removeFilter();
            return;
        }
        const updatedFilter = set(key, value, textFilter);
        updateFilter(updatedFilter);
    }, [removeFilter, updateFilter, filterTextValue, textFilter]);

    const updateTextValueKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => e.key.toLowerCase() === 'enter' && updateTextFilter('value', filterTextValue), [updateTextFilter, filterTextValue]);
    const updateTextValueClick = useCallback(() => updateTextFilter('value', filterTextValue), [updateTextFilter, filterTextValue]);
    const clearTextValueFilter = useCallback(() => {
        setFilterTextValue('');
        updateTextFilter('value', '');
    }, [updateTextFilter]);

    const updateFuzzyMatchFilter = useCallback((value: boolean | undefined) => {
        if (!isUndefined(value)) {
            updateTextFilter('isFuzzyMatchFilter', !value);
        }
    }, [updateTextFilter]);

    return (
        <div className={styles.textFilterWrapper}>
            <div className={styles.textValueWrapper}>
                <input
                    placeholder='Add filter...'
                    className={styles.tableFilterInput}
                    data-testid={`${testId}-input`}
                    onChange={e => setFilterTextValue(e.target.value)}
                    onKeyDown={updateTextValueKeyDown}
                    value={filterTextValue}
                    autoFocus
                />
                <CustomTooltip overlayText='Apply Filter'>
                    <button onClick={updateTextValueClick} className={styles.setFilterButton}><Icon icon={Tick} fontSize={18} /></button>
                </CustomTooltip>
                {filterTextValue.length > 0 &&
                    <CustomTooltip overlayText='Clear Filter'>
                        <button onClick={clearTextValueFilter} className={styles.clearFilterButton}><Icon icon={Delete} fontSize={18} /></button>
                    </CustomTooltip>
                }
            </div>
            <ToggleFilter filter={!isFuzzyMatchFilter} testId={testId} updateFilter={updateFuzzyMatchFilter} labels={['Exact Match', 'Fuzzy Match']} />
        </div>
    );
};
